import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { INDUSTRIES, PRODUCTS, INDUSTRY_PRODUCTS } from "../data/products";

function IndustryIcon({ kind }) {
  const c = { width: 64, height: 64, viewBox: "0 0 80 80", fill: "none", strokeLinecap: "round", strokeLinejoin: "round", stroke: "currentColor", strokeWidth: 1.4 };
  const steel = "#2E70AD";
  if (kind === "oil") return (<svg {...c}><rect x="18" y="24" width="14" height="44" /><rect x="36" y="12" width="12" height="56" /><rect x="52" y="30" width="10" height="38" /><path d="M18 24 Q 25 18 32 24" /><path d="M36 12 Q 42 6 48 12" /><line x1="18" y1="40" x2="62" y2="40" strokeDasharray="2 4" opacity=".4" /></svg>);
  if (kind === "power") return (<svg {...c}><circle cx="40" cy="40" r="6" />{[0, 60, 120, 180, 240, 300].map((a) => { const ax = 40 + Math.cos((a * Math.PI) / 180) * 6, ay = 40 + Math.sin((a * Math.PI) / 180) * 6, bx = 40 + Math.cos((a * Math.PI) / 180) * 22, by = 40 + Math.sin((a * Math.PI) / 180) * 22; return <g key={a}><path d={`M${ax} ${ay} Q ${bx - 10} ${by - 10} ${bx} ${by}`} /><ellipse cx={bx} cy={by} rx="10" ry="4" transform={`rotate(${a + 30} ${bx} ${by})`} opacity=".6" /></g>; })}</svg>);
  if (kind === "infra") return (<svg {...c}><path d="M8 52 Q 40 12 72 52" /><line x1="8" y1="52" x2="72" y2="52" /><line x1="14" y1="52" x2="14" y2="62" /><line x1="24" y1="52" x2="24" y2="62" /><line x1="40" y1="34" x2="40" y2="62" /><line x1="56" y1="52" x2="56" y2="62" /><line x1="66" y1="52" x2="66" y2="62" /><line x1="8" y1="68" x2="72" y2="68" /></svg>);
  if (kind === "mine") return (<svg {...c}><path d="M8 60 L24 30 L40 44 L52 26 L72 60 Z" /><path d="M24 30 L28 20 L34 24" /></svg>);
  if (kind === "chem") return (<svg {...c}><path d="M28 14 L28 28 L16 56 Q 16 64 24 64 L56 64 Q 64 64 64 56 L52 28 L52 14 Z" /><line x1="22" y1="14" x2="58" y2="14" /><circle cx="32" cy="48" r="2" fill={steel} /><circle cx="42" cy="54" r="2" fill={steel} opacity=".6" /><circle cx="50" cy="46" r="2" fill={steel} opacity=".8" /></svg>);
  if (kind === "mfg") return (<svg {...c}><rect x="10" y="30" width="60" height="36" /><path d="M10 30 L22 18 L34 30" /><path d="M34 30 L46 18 L58 30" /><rect x="18" y="44" width="10" height="16" fill={steel} opacity=".3" /><rect x="36" y="44" width="10" height="16" fill={steel} opacity=".3" /><rect x="54" y="44" width="10" height="16" fill={steel} opacity=".3" /></svg>);
  if (kind === "marine") return (<svg {...c}><path d="M10 52 L70 52 L62 68 L18 68 Z" /><line x1="40" y1="16" x2="40" y2="52" /><path d="M40 16 L58 48 L40 48 Z" fill={steel} fillOpacity=".2" /><path d="M40 22 L22 48 L40 48 Z" fill={steel} fillOpacity=".15" /></svg>);
  return null;
}

export function Industries() {
  const [activeRow, setActiveRow] = useState(null); // industry.key
  const [activeCol, setActiveCol] = useState(null); // product.slug
  const matrixRef = useRef(null);
  const lockUntil = useRef(0);

  const jumpToMatrix = (indKey) => {
    setActiveRow(indKey);
    setActiveCol(null);
    lockUntil.current = Date.now() + 900; // suppress hover-overrides during scroll
    const el = matrixRef.current;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const hoverRow = (k) => { if (Date.now() >= lockUntil.current) setActiveRow(k); };
  const hoverCol = (s) => { if (Date.now() >= lockUntil.current) setActiveCol(s); };
  const hoverCell = (k, s) => { if (Date.now() >= lockUntil.current) { setActiveRow(k); setActiveCol(s); } };

  return (
    <>
      <section className="industries reveal" id="industries" data-testid="industries-section">
        <div className="container">
          <div className="industries-head">
            <div className="section-label"><span className="dot" />Industries we serve</div>
            <h2 className="industries-h2 display" data-testid="industries-h2">
              Seven industries. <span style={{ color: "var(--text-3)" }}>Twenty-four sub-segments. One vocabulary.</span>
            </h2>
          </div>
          <div className="industries-grid">
            {INDUSTRIES.map((ind, i) => {
              const prods = INDUSTRY_PRODUCTS[ind.key] || [];
              return (
                <button
                  className="industry-card reveal"
                  key={ind.name}
                  style={{ "--d": `${i * 100}ms` }}
                  data-testid={`industry-card-${ind.key}`}
                  onClick={() => jumpToMatrix(ind.key)}
                  type="button"
                >
                  <div className="industry-card-head">
                    <div className="industry-icon"><IndustryIcon kind={ind.icon} /></div>
                    <div className="industry-count mono">{prods.length}<span>/8</span></div>
                  </div>
                  <h3 className="industry-name">{ind.name}</h3>
                  <div className="industry-segs">
                    {ind.segs.map((s, j) => <span className="industry-seg" key={j}>{s}</span>)}
                  </div>
                  <div className="industry-foot">
                    <span className="industry-foot-label">{ind.segs.length} sub-segments</span>
                    <span className="industry-foot-cta">
                      View in matrix <span className="arrow">→</span>
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== Product × Industry Matrix (interactive) ===== */}
      <section className="industry-matrix" data-testid="industry-matrix" ref={matrixRef}>
        <div className="container">
          <div className="matrix-head">
            <div>
              <div className="section-label"><span className="dot" />Product × Industry matrix</div>
              <h2 className="matrix-h display" data-testid="matrix-h">
                Which of our eight platforms <span style={{ color: "var(--text-3)" }}>applies to your industry.</span>
              </h2>
              <p className="matrix-sub">
                {activeRow || activeCol
                  ? activeRow
                    ? <>Showing: <strong>{INDUSTRIES.find((i) => i.key === activeRow)?.name}</strong> · {(INDUSTRY_PRODUCTS[activeRow] || []).length} best-fit platforms</>
                    : <>Showing: <strong>{PRODUCTS.find((p) => p.slug === activeCol)?.indo}</strong> · deploys across {PRODUCTS.find((p) => p.slug === activeCol)?.industries.length} industries</>
                  : "Hover or click a row or column to filter. Click an intersection for platform detail."}
              </p>
            </div>
            <div className="matrix-actions">
              <button
                className="matrix-reset"
                onClick={() => { setActiveRow(null); setActiveCol(null); }}
                data-testid="matrix-reset"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="matrix-grid-wrap">
            <div
              className="matrix-grid"
              style={{ gridTemplateColumns: `minmax(180px, 1.2fr) repeat(${PRODUCTS.length}, minmax(76px, 1fr))` }}
              data-testid="matrix-grid"
            >
              {/* Corner + column heads */}
              <div className="matrix-corner" aria-hidden="true"></div>
              {PRODUCTS.map((p) => (
                <button
                  key={p.slug}
                  type="button"
                  className={"matrix-col-head " + (activeCol === p.slug ? "is-active" : "")}
                  onMouseEnter={() => hoverCol(p.slug)}
                  onFocus={() => hoverCol(p.slug)}
                  onClick={() => setActiveCol(activeCol === p.slug ? null : p.slug)}
                  data-testid={`matrix-col-${p.slug}`}
                >
                  <div className="matrix-col-cat mono">{p.cat === "A" ? "CAT · A" : "CAT · B"}</div>
                  <div className="matrix-col-indo">{p.indo}</div>
                  <div className="matrix-col-dev">{p.devnagari}</div>
                </button>
              ))}

              {/* Rows */}
              {INDUSTRIES.map((ind) => (
                <React.Fragment key={ind.key}>
                  <button
                    type="button"
                    className={"matrix-row-head " + (activeRow === ind.key ? "is-active" : "")}
                    onMouseEnter={() => hoverRow(ind.key)}
                    onFocus={() => hoverRow(ind.key)}
                    onClick={() => setActiveRow(activeRow === ind.key ? null : ind.key)}
                    data-testid={`matrix-row-${ind.key}`}
                  >
                    <span className="matrix-row-icon"><IndustryIcon kind={ind.icon} /></span>
                    <span className="matrix-row-name">{ind.name}</span>
                  </button>

                  {PRODUCTS.map((p) => {
                    const fit = p.industries.includes(ind.key);
                    const rowActive = activeRow === ind.key;
                    const colActive = activeCol === p.slug;
                    const active = rowActive || colActive;
                    const anyActive = activeRow || activeCol;
                    const dim = anyActive && !active;
                    return (
                      <Link
                        key={p.slug}
                        to={`/platforms/${p.slug}`}
                        className={
                          "matrix-cell " +
                          (fit ? "is-fit " : "is-nofit ") +
                          (dim ? "is-dim " : "") +
                          (rowActive && colActive ? "is-focus " : "")
                        }
                        onMouseEnter={() => hoverCell(ind.key, p.slug)}
                        data-testid={`matrix-cell-${ind.key}-${p.slug}`}
                        aria-label={`${p.indo} · ${ind.name}: ${fit ? "fit" : "not a fit"}`}
                      >
                        <span className="matrix-dot" aria-hidden="true"></span>
                      </Link>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="matrix-legend mono">
            <span className="matrix-legend-swatch matrix-legend-fit" /> deploys in this industry
            <span className="matrix-legend-swatch matrix-legend-nofit" /> not a fit
            <span className="matrix-legend-note">click any cell to open the platform drilldown</span>
          </div>
        </div>
      </section>
    </>
  );
}
